import dayjs, { Dayjs } from 'dayjs';
import { ReviewPeriodDayType } from '../api-client/autogenerated';

/** The working-day columns shared by subscribers (defaults) and projects (overrides). */
export interface WorkingDaySettingsSource {
  reviewPeriodDayType?: ReviewPeriodDayType | null;
  workingDays?: number[] | null;
  documentCutoffTime?: string | null;
  timeZone?: string | null;
}

/** Form state: never null, so inputs stay controlled. '' means "not set". */
export interface WorkingDaySettingsDraft {
  reviewPeriodDayType: ReviewPeriodDayType;
  workingDays: number[];
  documentCutoffTime: string;
  timeZone: string;
}

/** ISO weekday numbers (1 = Monday .. 7 = Sunday), matching the backend's convention. */
export const WEEKDAY_OPTIONS = [
  { isoWeekday: 1, label: 'Mon' },
  { isoWeekday: 2, label: 'Tue' },
  { isoWeekday: 3, label: 'Wed' },
  { isoWeekday: 4, label: 'Thu' },
  { isoWeekday: 5, label: 'Fri' },
  { isoWeekday: 6, label: 'Sat' },
  { isoWeekday: 7, label: 'Sun' },
];

export const DEFAULT_WORKING_DAYS = [1, 2, 3, 4, 5];

/** Custom mode's default cutoff when nothing has been configured yet: 5:00 PM. */
export const DEFAULT_DOCUMENT_CUTOFF_TIME = '17:00';

/**
 * Builds form state from the first source that has each field set — pass the project first and
 * its subscriber second to show a project's effective settings.
 */
export const draftFromWorkingDaySettings = (
  ...sources: (WorkingDaySettingsSource | null | undefined)[]
): WorkingDaySettingsDraft => {
  const first = <T>(values: (T | null | undefined)[]) =>
    values.find((value) => value !== null && value !== undefined);
  return {
    reviewPeriodDayType:
      first(sources.map((source) => source?.reviewPeriodDayType)) ?? ReviewPeriodDayType.Calendar,
    workingDays: [...(first(sources.map((source) => source?.workingDays)) ?? DEFAULT_WORKING_DAYS)],
    documentCutoffTime:
      first(sources.map((source) => source?.documentCutoffTime)) ?? DEFAULT_DOCUMENT_CUTOFF_TIME,
    timeZone: first(sources.map((source) => source?.timeZone)) ?? '',
  };
};

/** Parses the stored 'HH:mm' cutoff into a time-picker value; '' (no cutoff) becomes null. */
export const parseCutoffTime = (documentCutoffTime: string): Dayjs | null =>
  documentCutoffTime ? dayjs(`2000-01-01T${documentCutoffTime}`) : null;

/** Formats a time-picker value back to the stored 'HH:mm'; null (cleared) becomes ''. */
export const formatCutoffTime = (date: Dayjs | null): string => (date ? date.format('HH:mm') : '');

/** The request payload for a draft; empty strings become null (no cutoff / no time zone). */
export const workingDaySettingsFromDraft = (draft: WorkingDaySettingsDraft) => ({
  reviewPeriodDayType: draft.reviewPeriodDayType,
  workingDays: [...draft.workingDays].sort((a, b) => a - b),
  documentCutoffTime: draft.documentCutoffTime || null,
  timeZone: draft.timeZone || null,
});

/** Only the "at least one working day" check — shown inline under the weekday checkboxes. */
export const getWorkingDaysSelectionError = (draft: WorkingDaySettingsDraft): string | undefined =>
  draft.reviewPeriodDayType === ReviewPeriodDayType.Working && draft.workingDays.length === 0
    ? 'Select at least one working day.'
    : undefined;

/**
 * Only the "time zone required" check — shown inline under the time zone select. The field is
 * only shown (and so only required) in Custom mode; Calendar mode never looks at the time zone.
 */
export const getTimeZoneError = (
  draft: WorkingDaySettingsDraft,
  required: boolean | undefined,
): string | undefined =>
  required && draft.reviewPeriodDayType === ReviewPeriodDayType.Working && !draft.timeZone
    ? 'Select a time zone.'
    : undefined;

export interface WorkingDaySettingsValidationOptions {
  /** Whether the time zone is required on this page (e.g. Construction Management setup). */
  requireTimeZone?: boolean;
}

export const getWorkingDaySettingsError = (
  draft: WorkingDaySettingsDraft,
  options: WorkingDaySettingsValidationOptions = {},
): string | undefined =>
  getWorkingDaysSelectionError(draft) ?? getTimeZoneError(draft, options.requireTimeZone);
